/**
 * CONTRATO VINCULATIVO DO PEDIDO (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Um único objecto atravessa todo o fluxo:
 *   interpretação → handoff Premium/Pro → formulário → pesquisa → geração →
 *   reparação → UI → Guardar/PDF/Partilha.
 *
 * Regras globais (nunca por cidade, país ou rota concreta):
 *  1. Tudo o que o utilizador pediu é requisito verificável, não sugestão.
 *  2. Antes de entregar, o resultado é validado contra o contrato. Faltando
 *     algo obrigatório, o roteiro NÃO é entregue como correcto.
 *  3. Faltando informação indispensável, faz-se uma pergunta curta e gratuita
 *     ANTES de IA, Maps ou créditos — nunca se devolve vazio nem se preenche
 *     com locais irrelevantes.
 */

import type { GeoScopeResult } from "./geo-scope";
import { parseIntradayWindow, expiredWindowQuestion } from "./intraday-window";
import { buildTripPreferences, type TripPreferences } from "./trip-preferences";
import { extractTripRequirements, type TripRequirements } from "./trip-requirements";
import { categoriesInText, fold, matchesNamedItem } from "./catalog/taxonomy";
import {
  extractSignatureSpecialtyVenues,
  type LocalSpecialty,
} from "./local-specialties";
import type { MealPreference } from "./meal-preference";
import type { TransportMode } from "./transport-modes";
import type { TemplateCategory } from "./catalog/curated-route-templates";
import { parseDayAllocations, checkDayAllocations, type DayThemeAllocation } from "./day-allocation";

export type ContractSource = "text" | "form" | "default";

export type DayPeriod = "morning" | "afternoon" | "evening";

/** Percurso híbrido: motorizado entre zonas, pedonal dentro de cada zona. */
export type TransportPlan = {
  primary: TransportMode;
  secondary: TransportMode[];
  /** True quando o pedido combina explicitamente dois modos. */
  hybrid: boolean;
  source: ContractSource;
};

export type NamedFoodAnchor = {
  query: string;
  kinds: LocalSpecialty["kinds"];
  /** Estabelecimento de referência quando o contexto é inequívoco. */
  signatureVenue?: string;
};

export type RequestContract = {
  text: string;
  language: string;
  scope: GeoScopeResult;
  /** Dias pedidos (ou inferidos). */
  days: number;
  daysSource: ContractSource;
  /** Orçamento real de tempo, em horas, quando o pedido é do próprio dia. */
  timeBudgetHours?: number;
  /** Fracção mínima do tempo que a rota tem de usar. */
  minTimeUsageRatio: number;
  /** Períodos explicitamente pedidos. */
  periods: DayPeriod[];
  preferences: TripPreferences;
  requirements: TripRequirements;
  namedFoodAnchors: NamedFoodAnchor[];
  /** Distribuição temática pedida ("praia em 2 dos 3 dias"). */
  dayAllocations: DayThemeAllocation[];
  transport: TransportPlan;
  corridorId?: string;
  mealPreference: MealPreference;
  /** A janela indicada já passou (pedir nova hora antes de gerar). */
  expiredWindow: boolean;
};

export type ContractQuestionOption = { id: string; label: string };

export type ContractQuestion = {
  code: "duration" | "expired_window" | "beach_period" | "named_item";
  question: string;
  options: ContractQuestionOption[];
  /** Perguntas do contrato são sempre gratuitas. */
  free: true;
};

export type ContractViolation = {
  code:
    | "empty_plan"
    | "days_mismatch"
    | "time_budget_short"
    | "excluded_category_present"
    | "excluded_named_item_present"
    | "missing_named_item"
    | "missing_required_category"
    | "missing_period"
    | "missing_transport_mode"
    | "corridor_missing"
    | "day_allocation_mismatch";
  message: string;
  /** Pode ser resolvido alargando a pesquisa, sem novo crédito. */
  expandable?: boolean;
};

export const MIN_TIME_USAGE_RATIO = 0.75;

/**
 * Categorias que se satisfazem umas às outras. Genérico e internacional: um
 * pedido de "comer" fica cumprido por restaurante, café, pastelaria ou
 * mercado, e vice-versa.
 */
const CATEGORY_FAMILIES: Record<string, string[]> = {
  restaurant: ["restaurant", "cafe", "bakery", "market"],
  cafe: ["cafe", "bakery", "restaurant"],
  bakery: ["bakery", "cafe"],
  market: ["market", "shopping"],
  nature: ["nature", "park", "viewpoint", "beach"],
  park: ["park", "nature"],
  museum: ["museum", "monument"],
  monument: ["monument", "museum", "castle", "church"],
};

// ---------------------------------------------------------------------------
// Interpretação
// ---------------------------------------------------------------------------

/** "tenho 7 horas", "I have 7 hours", "ho 7 ore", "ich habe 7 Stunden". */
const TIME_BUDGET_PATTERNS: RegExp[] = [
  /\b(?:tenho|temos|disponho|disponibilidade\s+de|com|durante|por)\s+(?:cerca\s+de\s+|about\s+)?(\d{1,2})(?:[.,](\d))?\s*(?:horas?|h)\b/u,
  /\b(?:i|we)\s+have\s+(?:about\s+)?(\d{1,2})(?:[.,](\d))?\s*(?:hours?|h)\b/u,
  /\b(?:tengo|tenemos)\s+(\d{1,2})(?:[.,](\d))?\s*horas?\b/u,
  /\b(?:j'ai|nous\s+avons)\s+(\d{1,2})(?:[.,](\d))?\s*heures?\b/u,
  /\b(?:ho|abbiamo)\s+(\d{1,2})(?:[.,](\d))?\s*ore\b/u,
  /\b(?:ich\s+habe|wir\s+haben)\s+(\d{1,2})(?:[.,](\d))?\s*stunden\b/u,
  /\b(\d{1,2})(?:[.,](\d))?\s*(?:horas?|hours?|heures?|ore|stunden)\b(?!\s*(?:da|de|of|the|do))/u,
];

/** Orçamento de tempo intradiário, em horas. */
export function parseTimeBudgetHours(text: string, now: Date = new Date()): number | undefined {
  const value = fold(text ?? "");
  if (!value.trim()) return undefined;
  // "3 dias" nunca é um orçamento de horas.
  for (const pattern of TIME_BUDGET_PATTERNS) {
    const match = value.match(pattern);
    if (!match) continue;
    const hours = Number(`${match[1]}${match[2] ? `.${match[2]}` : ""}`);
    if (!Number.isFinite(hours) || hours <= 0 || hours > 16) continue;
    return Math.round(hours * 10) / 10;
  }
  const window = parseIntradayWindow(text, now);
  return window?.durationHours;
}

const PERIOD_PATTERNS: Array<{ period: DayPeriod; re: RegExp }> = [
  {
    period: "morning",
    re: /\b(?:de\s+manha|pela\s+manha|manha|morning|por\s+la\s+manana|manana|matin|mattina|mattino|morgens?|vormittag)\b/u,
  },
  {
    period: "afternoon",
    re: /\b(?:a\s+tarde|de\s+tarde|tarde|afternoon|apres[- ]midi|pomeriggio|nachmittag)\b/u,
  },
  {
    period: "evening",
    re: /\b(?:a\s+noite|de\s+noite|noite|ao\s+por\s+do\s+sol|por\s+do\s+sol|sunset|evening|night|noche|soir|soiree|sera|serata|abend|nacht)\b/u,
  },
];

/** Períodos do dia explicitamente pedidos. */
export function parseDayPeriods(text: string): DayPeriod[] {
  const value = fold(text ?? "");
  const out: DayPeriod[] = [];
  for (const entry of PERIOD_PATTERNS) if (entry.re.test(value)) out.push(entry.period);
  return out;
}

const MODE_PATTERNS: Array<{ mode: TransportMode; re: RegExp }> = [
  { mode: "car", re: /\b(?:de\s+carro|carro|automovel|by\s+car|en\s+coche|coche|en\s+voiture|voiture|in\s+auto|auto|mit\s+dem\s+auto)\b/u },
  { mode: "motorcycle", re: /\b(?:de\s+mota|mota|moto|motorcycle|motorbike|motorrad)\b/u },
  { mode: "walk", re: /\b(?:a\s+pe|andar\s+a\s+pe|caminhar|caminhada|on\s+foot|walking|walk|a\s+pie|andando|a\s+piedi|zu\s+fuss|marche)\b/u },
  { mode: "bike", re: /\b(?:de\s+bicicleta|bicicleta|bike|cycling|velo|fahrrad)\b/u },
  { mode: "scooter", re: /\b(?:trotinete|trotineta|scooter|patinete|monopattino)\b/u },
  { mode: "transit", re: /\b(?:transportes?\s+publicos?|metro|comboio|autocarro|public\s+transport|transit|bus|train|treno|zug)\b/u },
];

const MODE_PRIORITY: TransportMode[] = ["car", "motorcycle", "transit", "bike", "scooter", "walk"];

/**
 * Modos de transporte pedidos. "vou de carro, mas posso andar a pé" produz um
 * plano híbrido: motorizado entre zonas, caminhada dentro de cada zona.
 */
export function parseTransportPlan(text: string, fallback: TransportMode = "walk"): TransportPlan {
  const value = fold(text ?? "");
  const found = MODE_PATTERNS.filter((entry) => entry.re.test(value)).map((entry) => entry.mode);
  if (found.length === 0) {
    return { primary: fallback, secondary: [], hybrid: false, source: "default" };
  }
  const ordered = MODE_PRIORITY.filter((mode) => found.includes(mode));
  const [primary, ...secondary] = ordered;
  return { primary, secondary, hybrid: ordered.length > 1, source: "text" };
}

export type BuildRequestContractInput = {
  text: string;
  language?: string;
  /** Âmbito escrito no formulário (cidades), quando existir. */
  scopeText?: string;
  formInterests?: string[];
  mealChoice?: MealPreference | null;
  eatMinutes?: number | null;
  /** Dias escolhidos no formulário. */
  formDays?: number | null;
  /** Transporte escolhido no formulário. */
  formTransport?: TransportMode | null;
  corridorId?: string | null;
  maxDeviationKm?: number | null;
  curatedExperienceChosen?: boolean;
  curatedExperienceCategory?: TemplateCategory;
  now?: Date;
};

/**
 * Constrói o contrato a partir do estado ACTUAL. Determinístico e sem
 * herança: um texto novo apaga por completo o contrato anterior.
 */
export function buildRequestContract(input: BuildRequestContractInput): RequestContract {
  const text = input.text ?? "";
  const language = (input.language ?? "pt").slice(0, 2).toLowerCase();
  const now = input.now ?? new Date();

  const preferences = buildTripPreferences({
    text,
    formInterests: input.formInterests,
    mealChoice: input.mealChoice ?? null,
    eatMinutes: input.eatMinutes ?? null,
    corridorId: input.corridorId ?? null,
    maxDeviationKm: input.maxDeviationKm ?? null,
  });

  const requirements = extractTripRequirements(text, {
    language,
    scopeText: input.scopeText,
    curatedExperienceChosen: input.curatedExperienceChosen,
    curatedExperienceCategory: input.curatedExperienceCategory,
  });

  const window = parseIntradayWindow(text, now);
  const timeBudgetHours = parseTimeBudgetHours(text, now);

  // Ordem vinculativa da duração: dias escritos > horas escritas > formulário.
  // Horas NUNCA podem virar dias, e um valor antigo do formulário nunca pode
  // sobrepor-se a uma janela de horas indicada no texto.
  const days = requirements.requestedDays
    ? requirements.requestedDays
    : timeBudgetHours
      ? 1
      : input.formDays && input.formDays > 0
        ? input.formDays
        : 1;
  const daysSource: ContractSource = requirements.requestedDays
    ? "text"
    : timeBudgetHours
      ? "text"
      : input.formDays
        ? "form"
        : "default";

  const transport = parseTransportPlan(text, input.formTransport ?? "walk");

  const namedFoodAnchors: NamedFoodAnchor[] = [];
  for (const signature of extractSignatureSpecialtyVenues(text)) {
    namedFoodAnchors.push({
      query: signature.query,
      kinds: signature.kinds,
      signatureVenue: signature.venue,
    });
  }
  for (const specialty of preferences.foodSpecialties) {
    const term = fold(specialty.query);
    const covered = namedFoodAnchors.some((anchor) => {
      const existing = fold(anchor.query);
      return existing.includes(term) || term.includes(existing);
    });
    if (covered) continue;
    namedFoodAnchors.push({ query: specialty.query, kinds: specialty.kinds });
  }

  return {
    text,
    language,
    scope: requirements.scope,
    days,
    daysSource,
    timeBudgetHours,
    minTimeUsageRatio: MIN_TIME_USAGE_RATIO,
    periods: parseDayPeriods(text),
    preferences,
    requirements,
    namedFoodAnchors,
    dayAllocations: parseDayAllocations(text, days),
    transport,
    corridorId: preferences.routeConstraints.corridorId,
    mealPreference: preferences.mealPreference,
    expiredWindow: window?.expired === true,
  };
}

// ---------------------------------------------------------------------------
// Validação do resultado contra o contrato
// ---------------------------------------------------------------------------

export type ContractPlanStop = {
  name: string;
  category?: string | null;
  period?: DayPeriod | null;
  minutes?: number | null;
  /** Modo usado para CHEGAR a esta paragem. */
  transportMode?: TransportMode | null;
};

export type ContractPlanDay = {
  city?: string | null;
  stops: ContractPlanStop[];
  /** Minutos efectivamente planeados no dia (visitas + deslocações). */
  plannedMinutes?: number | null;
  /** Pontos de passagem verificados do corredor obrigatório. */
  corridorWaypoints?: string[] | null;
  /** Temas efetivamente cumpridos no dia ("beach", "rest"…). */
  themes?: string[] | null;
};

export type ContractPlan = { days: ContractPlanDay[] };

export type ContractValidation = {
  satisfied: boolean;
  violations: ContractViolation[];
};

const stopText = (stop: ContractPlanStop) => `${stop.name ?? ""} ${stop.category ?? ""}`.trim();

/** Valida o roteiro produzido contra o contrato. Puro e sem custo. */
export function validateRequestContract(
  contract: RequestContract,
  plan: ContractPlan,
): ContractValidation {
  const violations: ContractViolation[] = [];
  const days = plan?.days ?? [];
  const stops = days.flatMap((day) => day.stops ?? []);

  if (stops.length === 0) {
    return {
      satisfied: false,
      violations: [{ code: "empty_plan", message: "O roteiro não tem paragens reais.", expandable: true }],
    };
  }

  if (contract.daysSource !== "default" && days.length !== contract.days) {
    violations.push({
      code: "days_mismatch",
      message: `Pediste ${contract.days} dia(s) e o roteiro tem ${days.length}.`,
    });
  }

  // Exclusões rígidas: categorias e nomes próprios.
  for (const stop of stops) {
    const categories = categoriesInText(stopText(stop));
    const clash = categories.find((category) =>
      contract.preferences.excludedCategories.includes(category),
    );
    if (clash) {
      violations.push({
        code: "excluded_category_present",
        message: `"${stop.name}" pertence a uma categoria que excluíste (${clash}).`,
      });
    }
    const named = contract.preferences.excludedNamedItems.find((item) =>
      matchesNamedItem(stopText(stop), item),
    );
    if (named) {
      violations.push({
        code: "excluded_named_item_present",
        message: `"${stop.name}" corresponde a algo que excluíste (${named}).`,
      });
    }
  }

  // Itens nomeados obrigatórios (comidas, locais, experiências).
  const cityNames = days.map((day) => day.city ?? "").filter(Boolean);
  for (const item of contract.preferences.requiredNamedItems) {
    // Um destino ("Lisboa") cumpre-se sendo a cidade do dia, não uma paragem.
    if (cityNames.some((city) => matchesNamedItem(city, item))) continue;
    if (!stops.some((stop) => matchesNamedItem(stopText(stop), item))) {
      violations.push({
        code: "missing_named_item",
        message: `Pediste "${item}" e não existe nenhuma paragem que o cumpra.`,
        expandable: true,
      });
    }
  }
  for (const anchor of contract.namedFoodAnchors) {
    const target = anchor.signatureVenue ?? anchor.query;
    if (!stops.some((stop) => matchesNamedItem(stopText(stop), target))) {
      violations.push({
        code: "missing_named_item",
        message: `Falta uma paragem real para "${target}".`,
        expandable: true,
      });
    }
  }

  // Categorias obrigatórias pedidas explicitamente. Uma pastelaria cumpre um
  // pedido genérico de "comer" — as famílias evitam falsos incumprimentos.
  for (const category of contract.preferences.requiredCategories) {
    const family = CATEGORY_FAMILIES[category] ?? [category];
    const present = stops.some((stop) =>
      categoriesInText(stopText(stop)).some((found) => family.includes(found)),
    );
    if (!present) {
      violations.push({
        code: "missing_required_category",
        message: `Pediste ${category} e o roteiro não inclui nenhum.`,
        expandable: true,
      });
    }
  }

  // Distribuição temática por dias: exatamente os dias pedidos por tema.
  if (contract.dayAllocations.length > 0) {
    const dayThemes = days.map((day) => {
      const explicit = day.themes ?? null;
      if (explicit && explicit.length) return explicit;
      const found = new Set<string>();
      for (const stop of day.stops ?? []) {
        for (const category of categoriesInText(stopText(stop))) found.add(category);
      }
      return [...found];
    });
    for (const violation of checkDayAllocations(contract.dayAllocations, dayThemes)) {
      violations.push({
        code: "day_allocation_mismatch",
        message: `Pediste ${violation.expected} dia(s) de "${violation.theme}" e o roteiro tem ${violation.found}.`,
        expandable: true,
      });
    }
  }

  // Orçamento de tempo: usar a maior parte do período disponível.
  if (contract.timeBudgetHours) {
    const planned = days.reduce((total, day) => total + Math.max(0, day.plannedMinutes ?? 0), 0);
    const requested = contract.timeBudgetHours * 60;
    if (planned < requested * contract.minTimeUsageRatio) {
      violations.push({
        code: "time_budget_short",
        message: `Tens ${contract.timeBudgetHours}h e o roteiro só ocupa ${Math.round(planned / 60 * 10) / 10}h.`,
        expandable: true,
      });
    }
  }

  // Períodos pedidos (manhã/tarde/noite).
  for (const period of contract.periods) {
    if (!stops.some((stop) => stop.period === period)) {
      violations.push({
        code: "missing_period",
        message: `Pediste algo para o período "${period}" e não há nada planeado nesse período.`,
        expandable: true,
      });
    }
  }

  // Transporte híbrido: todos os modos pedidos têm de aparecer em troços.
  if (contract.transport.hybrid) {
    const used = new Set(
      stops.map((stop) => stop.transportMode).filter(Boolean) as TransportMode[],
    );
    for (const mode of [contract.transport.primary, ...contract.transport.secondary]) {
      if (!used.has(mode)) {
        violations.push({
          code: "missing_transport_mode",
          message: `Disseste que podias usar "${mode}" e o roteiro não tem nenhum troço nesse modo.`,
        });
      }
    }
  }

  // Corredor obrigatório: cada dia tem de percorrer o corredor, não só visitar
  // cidades soltas.
  if (contract.corridorId) {
    const missing = days.filter((day) => (day.corridorWaypoints ?? []).length === 0);
    if (missing.length > 0) {
      violations.push({
        code: "corridor_missing",
        message: `${missing.length} dia(s) não mostram pontos de passagem verificados do corredor pedido.`,
      });
    }
  }

  return { satisfied: violations.length === 0, violations };
}

// ---------------------------------------------------------------------------
// Perguntas curtas e gratuitas (antes de IA, Maps ou créditos)
// ---------------------------------------------------------------------------

const QUESTIONS: Record<string, Record<string, string>> = {
  duration: {
    pt: "Quanto tempo tens disponível?",
    en: "How much time do you have?",
    es: "¿Cuánto tiempo tienes?",
    fr: "Combien de temps as-tu ?",
    it: "Quanto tempo hai?",
    de: "Wie viel Zeit hast du?",
  },
  beach: {
    pt: "Quando preferes aproveitar a praia?",
    en: "When do you prefer to enjoy the beach?",
    es: "¿Cuándo prefieres disfrutar de la playa?",
    fr: "Quand préfères-tu profiter de la plage ?",
    it: "Quando preferisci goderti la spiaggia?",
    de: "Wann möchtest du an den Strand?",
  },
};

const BEACH_OPTIONS: Record<string, string[]> = {
  pt: ["De manhã", "Durante a tarde", "Até ao pôr do sol"],
  en: ["In the morning", "During the afternoon", "Until sunset"],
  es: ["Por la mañana", "Durante la tarde", "Hasta el atardecer"],
  fr: ["Le matin", "L'après-midi", "Jusqu'au coucher du soleil"],
  it: ["Di mattina", "Nel pomeriggio", "Fino al tramonto"],
  de: ["Am Morgen", "Am Nachmittag", "Bis zum Sonnenuntergang"],
};

const pick = <T,>(map: Record<string, T>, language: string): T => map[language] ?? map.en ?? map.pt;

/**
 * Perguntas indispensáveis para cumprir o contrato. Todas gratuitas e
 * respondidas antes de qualquer chamada paga.
 */
export function contractQuestions(contract: RequestContract): ContractQuestion[] {
  const language = contract.language;
  const out: ContractQuestion[] = [];

  if (contract.expiredWindow) {
    out.push({
      code: "expired_window",
      question: expiredWindowQuestion(language),
      options: [],
      free: true,
    });
  }

  if (!contract.timeBudgetHours && contract.daysSource === "default") {
    out.push({
      code: "duration",
      question: pick(QUESTIONS.duration, language),
      options: [],
      free: true,
    });
  }

  // Praia pedida sem período definido: perguntar quando, para não deixar a
  // praia ocupar o dia inteiro e ignorar o resto do pedido.
  const wantsBeach =
    contract.preferences.requiredCategories.includes("beach") ||
    /\bpraias?\b|\bbeach(?:es)?\b|\bplayas?\b|\bplages?\b|\bspiagge?\b|\bstrand\b/iu.test(contract.text);
  const beachExcluded = contract.preferences.excludedCategories.includes("beach");
  const periodKnown = contract.periods.length > 0;
  if (wantsBeach && !beachExcluded && !periodKnown) {
    out.push({
      code: "beach_period",
      question: pick(QUESTIONS.beach, language),
      options: pick(BEACH_OPTIONS, language).map((label, index) => ({
        id: (["morning", "afternoon", "sunset"] as const)[index],
        label,
      })),
      free: true,
    });
  }

  return out;
}

/** Categorias e nomes proibidos, prontos para filtrar ANTES da pesquisa. */
export function searchExclusions(contract: RequestContract): {
  categories: string[];
  namedItems: string[];
  allowRestaurantSearch: boolean;
} {
  return {
    categories: [...contract.preferences.excludedCategories],
    namedItems: [...contract.preferences.excludedNamedItems],
    allowRestaurantSearch:
      contract.mealPreference === "restaurant" &&
      !contract.preferences.excludedCategories.includes("restaurant"),
  };
}
